import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { alpha, Box, useTheme } from '@mui/material';
import { TooltipHoverEnterDelayMs } from './tooltipTiming';
import { getSurfaceBorderColor } from '../../../theme/theme';
import { OperationProgressIndicator } from '../progress';

const CursorOffsetX = 14;
const CursorOffsetY = 16;

interface PointerPosition {
  x: number;
  y: number;
}

interface UseTooltipHoverLoadingIndicatorOptions {
  active?: boolean;
  delayMs?: number;
  enabled?: boolean;
}

interface UseTooltipHoverLoadingIndicatorResult {
  indicator: ReactNode;
  onMouseEnter: (event: MouseEvent<HTMLElement>) => void;
  onMouseMove: (event: MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  clear: () => void;
}

function TooltipHoverLoadingIndicator({ position }: { position: PointerPosition }) {
  const theme = useTheme();
  const surfaceBorderColor = getSurfaceBorderColor(theme);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <Box
      aria-hidden="true"
      data-testid="tooltip-hover-loading-indicator"
      sx={{
        position: 'fixed',
        left: position.x + CursorOffsetX,
        top: position.y + CursorOffsetY,
        zIndex: theme.zIndex.tooltip + 1,
        width: 24,
        height: 24,
        display: 'grid',
        placeItems: 'center',
        borderRadius: '50%',
        pointerEvents: 'none',
        color: theme.palette.primary.light,
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        border: `1px solid ${surfaceBorderColor}`,
        boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, 0.32)}`,
        backdropFilter: 'blur(6px)',
      }}
    >
      <OperationProgressIndicator
        size={14}
        thickness={5}
        color="inherit"
        ariaLabel="Loading tooltip"
      />
    </Box>,
    document.body
  );
}

export function useTooltipHoverLoadingIndicator({
  active = false,
  delayMs = TooltipHoverEnterDelayMs,
  enabled = true,
}: UseTooltipHoverLoadingIndicatorOptions = {}): UseTooltipHoverLoadingIndicatorResult {
  const [position, setPosition] = useState<PointerPosition | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const clear = useCallback(() => {
    clearHideTimer();
    setPosition(null);
  }, [clearHideTimer]);

  const startHideTimer = useCallback(() => {
    clearHideTimer();

    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      setPosition(null);
    }, delayMs);
  }, [clearHideTimer, delayMs]);

  const onMouseEnter = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!enabled || active) {
        return;
      }

      setPosition({ x: event.clientX, y: event.clientY });
      startHideTimer();
    },
    [active, enabled, startHideTimer]
  );

  const onMouseMove = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!enabled || active) {
        return;
      }

      setPosition((current) => {
        if (!current) {
          return current;
        }

        return { x: event.clientX, y: event.clientY };
      });
    },
    [active, enabled]
  );

  useEffect(() => {
    if (!enabled || active) {
      clear();
    }
  }, [active, clear, enabled]);

  useEffect(() => clearHideTimer, [clearHideTimer]);

  return {
    indicator: position ? <TooltipHoverLoadingIndicator position={position} /> : null,
    onMouseEnter,
    onMouseMove,
    onMouseLeave: clear,
    clear,
  };
}
